import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import TileCard from '../components/TileCard';

/**
 * My Work tile — 3 stat chips (open / in-process / overdue) + top N urgent
 * tasks. Each row clicks through to the workflow item detail page.
 *
 * Data sources:
 *   - api.getWorkflowItemCounts() → { active, overdue }
 *   - api.getMyWork({ status: 'active' }) → { owned, supervised, submitted }
 *
 * Counts: server returns `active` (non-completed), so we derive open vs
 * in-process client-side from the stage_type of each owned item.
 */
export default function MyWorkTile({ limit = 5 }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.getWorkflowItemCounts().catch(() => null),
      api.getMyWork({ status: 'active' }).catch(() => null),
    ]).then(([counts, work]) => {
      if (cancelled) return;
      if (!counts && !work) {
        setState({ loading: false, error: 'Could not load My Work', data: null });
        return;
      }
      setState({ loading: false, error: null, data: { counts, work } });
    });
    return () => { cancelled = true; };
  }, []);

  const items = state.data?.work?.owned || [];
  // Client-side derive: "in process" = active stage type; "open" = start stage
  const openCount = items.filter(i => i.stage_type === 'start').length;
  const inProcessCount = items.filter(i => i.stage_type === 'active').length;
  const overdueCount = state.data?.counts?.overdue ?? 0;

  // Server already pre-sorts: overdue first, then sla_deadline asc, then created_at desc.
  const topUrgent = items.slice(0, limit);

  return (
    <TileCard
      title="My Work"
      actionLabel="View all →"
      actionHref="/workflow/my-work"
      loading={state.loading}
      error={state.error}
    >
      <div className="dash-stat-row">
        <div className="dash-stat-chip">
          <div className="dash-stat-chip-value">{openCount}</div>
          <div className="dash-stat-chip-label">Open</div>
        </div>
        <div className="dash-stat-chip">
          <div className="dash-stat-chip-value">{inProcessCount}</div>
          <div className="dash-stat-chip-label">In Process</div>
        </div>
        <div className={`dash-stat-chip ${overdueCount > 0 ? 'overdue' : ''}`}>
          <div className="dash-stat-chip-value">{overdueCount}</div>
          <div className="dash-stat-chip-label">Overdue</div>
        </div>
      </div>

      {topUrgent.length === 0 ? (
        <div className="dash-card-empty">Nothing urgent on your plate.</div>
      ) : (
        <div className="dash-list">
          {topUrgent.map(item => {
            const overdue = item.sla_deadline && new Date(item.sla_deadline) < new Date();
            return (
              <Link
                key={item.id}
                to={`/workflow/items/${item.id}`}
                className={`dash-list-row ${overdue ? 'overdue' : ''}`}
              >
                <div>
                  <div className="dash-list-title">{item.title}</div>
                  <div className="dash-list-sub">
                    {item.workflow_name} · {item.stage_name}
                  </div>
                </div>
                <span className={`dash-list-meta ${overdue ? 'overdue' : ''}`}>
                  {formatSla(item.sla_deadline)}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </TileCard>
  );
}

function formatSla(deadline) {
  if (!deadline) return 'No SLA';
  const now = new Date();
  const d = new Date(deadline);
  const diffMs = d.getTime() - now.getTime();
  if (diffMs < 0) {
    const h = Math.ceil(Math.abs(diffMs) / (1000 * 60 * 60));
    return h < 24 ? `${h}h over` : `${Math.ceil(h / 24)}d over`;
  }
  const h = Math.floor(diffMs / (1000 * 60 * 60));
  return h < 24 ? `${h}h left` : `${Math.floor(h / 24)}d left`;
}
